import { Alert } from "@/src/lib/alert";
import { Text, TextInput as BrandTextInput } from "@/src/components/ui/BrandText";
import { border, brand, surface, text as uiText } from "@/src/theme/tokens";
import { useAuth } from "@/src/hooks/useAuth";
import { useActiveAccount } from "@/src/hooks/useActiveAccount";
import { createGroupConversation, getChatErrorMessage } from "@/src/lib/chat";
import { startIdentityDm } from "@/src/lib/directMessaging";
import { getSubjectLabel, getTrackLabel } from "@/src/lib/taxonomy";
import { getAccountType, getPrimaryIdentityBadge } from "@/src/lib/identity";
import { contentMaxWidths } from "@/src/theme/layout";
import { Ionicons } from "@/src/components/ui/ionicons";
import { WebBackButton } from "@/src/components/WebBackButton";
import { useRouter } from "expo-router";
import { collection, getFirestore, limit, onSnapshot, query, where } from "firebase/firestore";
import React from "react";
import { ActivityIndicator, FlatList, Platform, Pressable, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const BG = surface.secondary;
const ACCENT = brand.primary;
const CARD = surface.primary;
const BORDER = border.subtle;
const MUTED = uiText.muted;
const DIRECTORY_LIMIT = 50;
const GROUP_MEMBER_LIMIT = 200;

function normalizeSearch(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function titleFor(item: any) {
  return item?.displayName?.trim() || item?.institutionName?.trim() || (item?.username ? `@${String(item.username).replace(/^@/, "")}` : "Utilisateur");
}

function subtitleFor(item: any) {
  const accountType = getAccountType(item);
  const role = (getPrimaryIdentityBadge(item)?.label ?? "Utilisateur").replace("École / Institution", "École");
  const detail = accountType === "teacher"
    ? getSubjectLabel(String(item?.mainSubject ?? ""))
    : accountType === "student"
      ? getTrackLabel(String(item?.track ?? ""))
      : accountType === "institution"
        ? String(item?.institutionName || "École / Institution")
        : "";
  return [role, detail].filter(Boolean).join(" · ");
}

export default function NewConversation() {
  const router = useRouter();
  const { user } = useAuth();
  const { activeAccountId } = useActiveAccount();
  const [items, setItems] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [mode, setMode] = React.useState<"dm" | "group">("dm");
  const [groupName, setGroupName] = React.useState("");
  const [selectedMap, setSelectedMap] = React.useState<Record<string, boolean>>({});
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    if (!user?.uid) {
      setItems([]);
      setLoading(false);
      return;
    }
    const db = getFirestore();
    const normalized = normalizeSearch(debouncedSearch);
    const ref = mode === "dm"
      ? normalized.length >= 2
        ? query(
            collection(db, "accounts"),
            where("status", "==", "active"),
            where("searchPrefixes", "array-contains", normalized.slice(0, 32)),
            limit(DIRECTORY_LIMIT)
          )
        : query(collection(db, "accounts"), where("status", "==", "active"), limit(DIRECTORY_LIMIT))
      : query(collection(db, "publicProfiles"), limit(GROUP_MEMBER_LIMIT));
    setLoading(true);
    setError(null);
    const unsub = onSnapshot(ref, (snap) => {
      const next = snap.docs
        .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as any) }))
        .filter((item) => mode === "dm" ? item.id !== activeAccountId : item.id !== user.uid)
        .sort((a, b) => titleFor(a).localeCompare(titleFor(b), "fr", { sensitivity: "base" }));
      setItems(next);
      setLoading(false);
    }, (snapshotError) => {
      setItems([]);
      setLoading(false);
      setError(getChatErrorMessage(snapshotError, "Impossible de charger les profils."));
    });
    return () => unsub();
  }, [activeAccountId, debouncedSearch, mode, user?.uid]);

  React.useEffect(() => setSelectedMap({}), [mode]);

  const selectedIds = React.useMemo(() => Object.keys(selectedMap).filter((id) => selectedMap[id]), [selectedMap]);
  const selectedItems = React.useMemo(() => items.filter((item) => selectedMap[item.id]), [items, selectedMap]);
  const filteredItems = React.useMemo(() => {
    const term = normalizeSearch(search);
    if (!term || mode === "dm") return items;
    return items.filter((item) => normalizeSearch([
      item.username,
      item.displayName,
      item.institutionName,
      item.accountType,
      item.role,
      getPrimaryIdentityBadge(item)?.label,
      item.track,
      item.mainSubject,
    ].filter(Boolean).join(" ")).includes(term));
  }, [items, mode, search]);

  const toggleSelected = React.useCallback((id: string) => {
    setSelectedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const openDm = React.useCallback(async (recipientIdentityId: string) => {
    if (!activeAccountId || creating) return;
    try {
      setCreating(true);
      const result = await startIdentityDm(activeAccountId, recipientIdentityId);
      router.replace({ pathname: "/(tabs)/messages/direct/[id]", params: { id: result.conversation.id } });
    } catch (e: any) {
      Alert.alert("Erreur", getChatErrorMessage(e, "Impossible d'ouvrir cette discussion."));
    } finally {
      setCreating(false);
    }
  }, [activeAccountId, creating, router]);

  const createGroup = React.useCallback(async () => {
    if (!user?.uid || creating) return;
    const cleanName = groupName.trim();
    if (cleanName.length < 3) return Alert.alert("Erreur", "Le nom du groupe doit contenir au moins 3 caractères.");
    if (!selectedIds.length) return Alert.alert("Erreur", "Sélectionne au moins une personne.");
    try {
      setCreating(true);
      const conversationId = await createGroupConversation({ creatorUid: user.uid, memberUids: selectedIds, name: cleanName });
      router.replace({ pathname: "/(tabs)/messages/[id]", params: { id: conversationId } });
    } catch (e: any) {
      Alert.alert("Erreur", getChatErrorMessage(e, "Impossible de créer le groupe."));
    } finally {
      setCreating(false);
    }
  }, [creating, groupName, router, selectedIds, user?.uid]);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: BG }} edges={["top", "bottom", "left", "right"]}>
      <View style={{ alignSelf: "center", flex: 1, maxWidth: contentMaxWidths.feed, paddingHorizontal: 16, paddingTop: 10, width: "100%" }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          <WebBackButton fallbackHref="/(tabs)/messages" />
          <View style={{ flex: 1, minWidth: 0 }}>
            {Platform.OS === "web" ? (
              <Text style={{ color: uiText.primary, fontSize: 24, fontWeight: "900" }}>Nouvelle discussion</Text>
            ) : null}
            <Text style={{ color: MUTED, marginTop: 4 }}>{mode === "dm" ? "Écris avec l’identité actuellement active." : "Les groupes restent liés à ton compte utilisateur."}</Text>
          </View>
        </View>

        <View style={{ flexDirection: "row", gap: 10, marginTop: 14, marginBottom: 14 }}>
          {(["dm", "group"] as const).map((value) => (
            <Pressable key={value} onPress={() => setMode(value)} accessibilityRole="radio" accessibilityState={{ checked: mode === value }} style={{ flex: 1, minHeight: 44, borderRadius: 14, alignItems: "center", justifyContent: "center", backgroundColor: mode === value ? ACCENT : CARD, borderWidth: 1, borderColor: mode === value ? ACCENT : BORDER }}>
              <Text style={{ color: uiText.primary, fontWeight: "900" }}>{value === "dm" ? "Message direct" : "Créer un groupe"}</Text>
            </Pressable>
          ))}
        </View>

        {mode === "group" ? (
          <View style={{ backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 18, padding: 12, marginBottom: 12, gap: 10 }}>
            <BrandTextInput value={groupName} onChangeText={setGroupName} placeholder="Nom du groupe" placeholderTextColor={uiText.muted} style={{ color: uiText.primary, backgroundColor: surface.primary, borderWidth: 1, borderColor: BORDER, borderRadius: 12, paddingHorizontal: 12, paddingVertical: 12 }} />
            <Text style={{ color: MUTED }}>{selectedIds.length} membre{selectedIds.length > 1 ? "s" : ""} sélectionné{selectedIds.length > 1 ? "s" : ""}</Text>
            {selectedItems.length ? <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>{selectedItems.map((item) => <Pressable key={item.id} onPress={() => toggleSelected(item.id)} style={{ flexDirection: "row", gap: 6, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 999, backgroundColor: surface.selected }}><Text style={{ color: uiText.primary }}>{titleFor(item)}</Text><Ionicons name="close" size={14} color={uiText.primary} /></Pressable>)}</View> : null}
            <Pressable onPress={createGroup} disabled={creating} style={{ backgroundColor: ACCENT, paddingVertical: 12, borderRadius: 12, alignItems: "center", opacity: creating ? 0.7 : 1 }}><Text style={{ color: uiText.primary, fontWeight: "900" }}>{creating ? "Création…" : "Créer le groupe"}</Text></Pressable>
          </View>
        ) : null}

        <BrandTextInput value={search} onChangeText={setSearch} placeholder={mode === "dm" ? "Rechercher une identité…" : "Rechercher des membres…"} placeholderTextColor={uiText.muted} style={{ color: uiText.primary, backgroundColor: surface.primary, borderWidth: 1, borderColor: BORDER, borderRadius: 14, paddingHorizontal: 12, paddingVertical: 12, marginBottom: 12 }} />
        {error ? <Text style={{ color: uiText.danger, marginBottom: 12 }}>{error}</Text> : null}

        {loading ? <View style={{ flex: 1, alignItems: "center", justifyContent: "center", gap: 8 }}><ActivityIndicator color={uiText.primary} /><Text style={{ color: MUTED }}>Chargement…</Text></View> : (
          <FlatList
            data={filteredItems}
            keyExtractor={(item) => item.id}
            contentContainerStyle={{ paddingBottom: 24 }}
            keyboardShouldPersistTaps="handled"
            ListEmptyComponent={<View style={{ backgroundColor: CARD, borderWidth: 1, borderColor: BORDER, borderRadius: 16, padding: 16, marginTop: 8 }}><Text style={{ color: uiText.primary, fontWeight: "800" }}>Aucun résultat</Text></View>}
            renderItem={({ item }) => {
              const selected = !!selectedMap[item.id];
              return <Pressable onPress={() => mode === "dm" ? openDm(item.id) : toggleSelected(item.id)} disabled={creating} style={{ flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 8, padding: 12, borderRadius: 16, borderWidth: 1, borderColor: selected ? border.brand : BORDER, backgroundColor: selected ? surface.selected : CARD, opacity: creating ? 0.7 : 1 }}>
                <View style={{ width: 42, height: 42, borderRadius: 21, alignItems: "center", justifyContent: "center", backgroundColor: surface.elevated }}><Text style={{ color: uiText.primary, fontWeight: "900" }}>{titleFor(item)[0]?.toUpperCase() || "?"}</Text></View>
                <View style={{ flex: 1 }}><Text style={{ color: uiText.primary, fontWeight: "800" }}>{titleFor(item)}</Text><Text style={{ color: MUTED, marginTop: 3 }}>{subtitleFor(item)}</Text></View>
                {mode === "group" ? <Ionicons name={selected ? "checkmark-circle" : "ellipse-outline"} size={22} color={selected ? ACCENT : MUTED} /> : <Ionicons name="chevron-forward" size={18} color={MUTED} />}
              </Pressable>;
            }}
          />
        )}
      </View>
    </SafeAreaView>
  );
}
